import type { JSX } from 'react';
import { Box, Checkbox, FormGroup, FormParagraph } from '@ksyos/design-system';
import { useEffect } from 'react';
import type { AdditionalConclusionInCustomControlData } from '@common/customControls/bariatrics';
import type { BariPreMedConsultAssessmentData } from '@common/validation/customControls/bariPre/bariPreMedConsultAssessment/v1/bariPreMedConsultAssessmentSchemaV1';
import type { BariPreNurseSpecialistMedScreeningReportData } from '@common/validation/customControls/bariPre/bariPreNurseSpecialistMedScreeningReport/types';
import type { BariPreSecretarialAssistantMedScreeningReportData } from '@common/validation/customControls/bariPre/bariPreSecretarialAssistantMedScreeningReport/types';
import { additionalConclusionsInCustomControlData, TabakOpties } from '@common/customControls/bariatrics';
import { CustomControlCode } from '@common/enums';
import { getCustomControlData } from '@common/utils/processUtil';
import { ProcessContext } from '@frontend/components/pages/episodeCategory/carePath/process/processContext';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { useProcessActivityEditContext } from '@frontend/utils/hooks/useProcessActivityContext';

export function AanvullendeConclusies(): JSX.Element {
    const { data, onChange } = useProcessActivityEditContext<BariPreMedConsultAssessmentData>();
    const { process } = useDefinedContext(ProcessContext);

    useEffect(() => {
        return () => {
            const additionalConclusionsData: Record<string, boolean> = {};
            for (const additionalConclusion of additionalConclusionsInCustomControlData) {
                additionalConclusionsData[additionalConclusion.key] = false;
            }

            return onChange(additionalConclusionsData, Object.keys(additionalConclusionsData));
        };
    }, []);

    return (
        <>
            <FormGroup label="Aanvullende conclusies">
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={4}
                >
                    {getAdditionalConclusions().map(({ key, label }) => (
                        <Checkbox
                            checked={data[key] ?? false}
                            key={key}
                            label={label}
                            onChange={(event) => onChange({ [key]: event.target.checked }, key)}
                        />
                    ))}
                </Box>
            </FormGroup>
            {additionalConclusionsInCustomControlData
                .filter((conclusion) => data[conclusion.key])
                .map(({ key, label, text }) => (
                    <FormGroup
                        key={key}
                        label={`Conclusie ${label.toLowerCase()}`}
                    >
                        <FormParagraph>{text}</FormParagraph>
                    </FormGroup>
                ))}
        </>
    );

    function isSmoker(): boolean {
        const customControlData = getCustomControlData<BariPreSecretarialAssistantMedScreeningReportData>(
            process,
            CustomControlCode.BariPreSecretarialAssistentMedScreeningReport
        );

        if (customControlData && 'roken' in customControlData) {
            return customControlData.roken === 'Ja';
        }
        if (customControlData && 'tabak_gebruik' in customControlData && customControlData.tabak_gebruik !== null) {
            return [
                TabakOpties.rookt_dagelijks_tabak,
                TabakOpties.rookt_passief_tabak,
                TabakOpties.rookt_soms_tabak,
            ].includes(customControlData.tabak_gebruik);
        }

        return false;
    }

    function hasDiabetes(): boolean {
        const customControlData = getCustomControlData<BariPreNurseSpecialistMedScreeningReportData>(
            process,
            CustomControlCode.BariPreNurseSpecialistMedScreeningReport
        );

        return Boolean(customControlData?.diabetes_mellitus);
    }

    function getAdditionalConclusions(): AdditionalConclusionInCustomControlData[] {
        return additionalConclusionsInCustomControlData.filter(
            (conclusion) =>
                !(conclusion.key === 'conclusie_aanvullend_diabetes_mellitus' && !hasDiabetes()) &&
                !(conclusion.key === 'conclusie_aanvullend_roken' && !isSmoker())
        );
    }
}
